import {
	Heading,
	InputSearch,
	type Suggestions,
	Text
} from "@pamperedchef/design-system";
import type { HeadingTag } from "@/app/utilities/types";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";

import { useClickOutside } from "@/app/hooks/useClickOutside";

import * as styles from "../shared.module.scss";

export interface PartyItem {
	showPk: string;
	hostName: string;
	location: string;
	date: string;
}

export interface FindPartyProps {
	value?: string;
	headline?: string;
	headlineTag?: HeadingTag;
	inputLabel?: string;
	inputPlaceholder?: string;
	suggestions?: {
		label?: string;
		items?: PartyItem[];
		loading: boolean;
		noResultsMessage?: Suggestions["noResultsMessage"];
	};
	onSelection?: (key: string) => void;
	onInputChange?: (value: string) => void;
}

/**
 * FindParty Component
 */
export function FindParty({
	value = "",
	headline,
	headlineTag = "h4",
	inputLabel,
	inputPlaceholder,
	suggestions,
	onSelection,
	onInputChange
}: FindPartyProps): JSX.Element {
	const [isPopoverVisible, setIsPopoverVisible] = useState(false);
	const popverRef = useRef<HTMLInputElement>(null);
	const isOutsideClick = useClickOutside(popverRef);

	/**
	 * Change handler
	 */
	function handleInputChange(val: string): void {
		onInputChange?.(val);
	}

	/**
	 * Map suggestions data
	 * @param parties
	 */
	function mapPartiesToSuggestions(parties: Array<PartyItem>): Suggestions {
		return {
			items: parties.map((party) => ({
				url: `${party?.showPk}`,
				label: party?.hostName,
				supportiveText: [party?.location, party?.date]
					.filter(Boolean)
					.join(" — ")
			})),
			visible: isPopoverVisible,
			loading: suggestions?.loading || false,
			noResultsMessage: "No results found",
			placement: "bottom",
			motionProps: {},
			onSelection: (key): void => {
				onSelection?.(key);
			}
		};
	}

	const suggestedParties = useMemo(
		() => suggestions?.items || [],
		[suggestions?.items]
	);
	useEffect(() => {
		if (value.length > 2 || (suggestedParties.length ?? 0)) {
			setIsPopoverVisible(true);
		}

		if (isOutsideClick) {
			setIsPopoverVisible(false);
		}
	}, [isOutsideClick, suggestedParties, value]);

	return (
		<div className={styles["find-form"]}>
			<Heading className={styles["find-form__title"]} tag={headlineTag}>
				{headline}
			</Heading>

			<Text className={styles["find-form__label"]} styledAs="smr">
				{inputLabel}
			</Text>

			<InputSearch
				aria-label={inputLabel}
				id="party-search"
				className={styles["find-form__input"]}
				placeholder={inputPlaceholder}
				suggestions={mapPartiesToSuggestions(suggestions?.items || [])}
				value={value}
				onChange={handleInputChange}
				customRef={popverRef as RefObject<HTMLInputElement>}
			/>
		</div>
	);
}
